import { useState } from "react";
import { AddReferralForm } from "pages/patient/PatientReferralsPage/components/AddReferralForm/AddReferralForm.jsx";
import { getPrefillErrorLabels } from "pages/patient/PatientReferralsPage/components/AddReferralForm/utils/getPrefillErrorLabels";

import { ConsultationReferralsTable } from "components/AddReferralForm/ConsultationReferralsTable";
import { HealthlinkCredentialsModal } from "components/HealthlinkCredentialsModal/HealthlinkCredentialsModal.jsx";
import { useConsultationsList } from "features/consultations/hooks/useConsultationsList";
import { usePatientFamilyMedicalHistory } from "features/family/hooks/usePatientFamilyMedicalHistory";
import { useHealthlinkCredentials } from "features/healthlink/hooks/useHealthlinkCredentials";
import { useHealthlinkPractice } from "features/healthlink/hooks/useHealthlinkPractice";
import { usePatientLongTermMedications } from "features/medications/hooks/usePatientLongTermMedications";
import { useAllergies } from "features/patients/hooks/useAllergies";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { useMedicalHistory } from "features/patients/hooks/useMedicalHistory";
import { usePatientBaseline } from "features/patients/hooks/usePatientBaseline";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { usePatientPreferences } from "features/patients/hooks/usePatientPreferences";
import { usePatientSocial } from "features/patients/hooks/usePatientSocial";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { useActiveConsultationReferral } from "features/referrals/hooks/useActiveConsultationReferral";
import { useReferralsPaginatedList } from "features/referrals/hooks/useReferralsPaginatedList";

import s from "./tabs.module.css";

export const ReferralTab = ({
  consultationId,
  onSuccess,
  isReadOnly = false,
  formWrapperStyle,
  resetOnSuccess = false,
  showAllReferrals = false,
}) => {
  const patientId = useCurrentPatientId();
  const [formKey, setFormKey] = useState(0);

  const { data: patient } = usePatientData();
  const { data: staffInfo } = useStaffInfo();
  const { data: healthlinkPractice, isLoading: isLoadingPractice } = useHealthlinkPractice();
  const { data: healthlinkCredentials, isLoading: isLoadingCredentials } = useHealthlinkCredentials();
  const { data: activeConsultationReferral } = useActiveConsultationReferral(consultationId);
  const { data: allReferralsData } = useReferralsPaginatedList({
    page: 1,
    patientId,
    sorting: "newest-first",
    rows: 100,
  });

  const isLoadingHealthlink = isLoadingPractice || isLoadingCredentials;
  const hasGpDetailsSaves =
    !!healthlinkPractice &&
    Array.isArray(healthlinkCredentials) &&
    healthlinkCredentials.length > 0;

  const { data: patientBaseline, isError: isPatientBaselineError } =
    usePatientBaseline(patientId);
  const { data: patientPreferences } = usePatientPreferences(patientId);
  const { data: patientSocial, isError: isPatientSocialError } =
    usePatientSocial(patientId);
  const { data: medicalHistory, isError: isMedicalHistoryError } =
    useMedicalHistory(patientId);
  const { data: familyMedicalHistory, isError: isFamilyMedicalHistoryError } =
    usePatientFamilyMedicalHistory(patientId);
  const { data: allergies, isError: isAllergiesError } =
    useAllergies(patientId);
  const { data: longTermMedications, isError: isLongTermMedicationsError } =
    usePatientLongTermMedications(patientId);
  const { data: consultationsList } = useConsultationsList(patientId);

  const prefillErrors = getPrefillErrorLabels({
    allergies: isAllergiesError,
    medicalHistory: isMedicalHistoryError,
    familyMedicalHistory: isFamilyMedicalHistoryError,
    longTermMedications: isLongTermMedicationsError,
    patientSocial: isPatientSocialError,
    patientBaseline: isPatientBaselineError,
  });

  const referralsToShow = showAllReferrals
    ? (allReferralsData?.data ?? [])
    : (activeConsultationReferral ?? []);

  if (isReadOnly) {
    return <ConsultationReferralsTable referrals={referralsToShow} />;
  }

  return (
    <>
      {!isLoadingHealthlink && !hasGpDetailsSaves ? (
        <HealthlinkCredentialsModal style={{ maxWidth: "360px", margin: "0 auto" }} />
      ) : (
        <AddReferralForm
          key={formKey}
          consultationId={consultationId}
          patientId={patientId}
          patient={patient}
          staffMember={staffInfo?.staffMember}
          referral={activeConsultationReferral?.at(0) || undefined}
          wrapperStyle={formWrapperStyle ?? { marginLeft: "-24px", marginRight: "-24px", marginBottom: "-24px" }}
          onSuccess={() => {
            if (resetOnSuccess) setFormKey((k) => k + 1);
            onSuccess?.();
          }}
          patientBaseline={patientBaseline}
          patientPreferences={patientPreferences}
          patientSocial={patientSocial}
          medicalHistory={medicalHistory}
          familyMedicalHistory={familyMedicalHistory}
          allergies={allergies}
          longTermMedications={longTermMedications}
          consultationsList={consultationsList}
          prefillErrors={prefillErrors}
        />
      )}
      <div className={s.sectionWrapper}>
        <div className={s.sectionHeader}>
          <h3 className={s.sectionTitle}>Patient Referrals</h3>
        </div>
        <div className={s.sectionContent}>
          <ConsultationReferralsTable referrals={referralsToShow} />
        </div>
      </div>
    </>
  );
};
